/**
 * FikoSPHKapYonetici.ts
 * Container (Mesh) Manager for SPH Fluid Simulation
 *
 * Converts 3D meshes into Signed Distance Fields (SDF) for efficient collision detection
 * Manages multiple containers and provides GPU buffers for shader access
 */

import type { Vec3 } from '../FikoSPHParametreler';

/**
 * Container/vessel information
 */
export interface KapBilgi {
  id: string;
  ad: string;
  sdfBuffer: GPUBuffer | null;
  sdfInfoBuffer: GPUBuffer | null;
  boyut: Vec3; // AABB size in meters
  cozunurluk: [number, number, number]; // SDF grid dimensions (x, y, z)
  hazir: boolean; // Ready for use
  vertexCount?: number;
  triangleCount?: number;
  aabbMin?: Vec3;
  aabbMax?: Vec3;
}

/**
 * SDF Info structure (must match shader struct)
 */
interface SDFInfo {
  originX: number;
  originY: number;
  originZ: number;
  cellSize: number;
  dimX: number;
  dimY: number;
  dimZ: number;
  totalCells: number;
}

/**
 * SDF Generation parameters (must match shader struct)
 */
interface SDFGenParams {
  originX: number;
  originY: number;
  originZ: number;
  cellSize: number;
  dimX: number;
  dimY: number;
  dimZ: number;
  triCount: number;
}

/**
 * Container Manager Class
 * Handles mesh-to-SDF conversion and buffer management
 */
export class FikoSPHKapYonetici {
  private kaplar: Map<string, KapBilgi> = new Map();
  private device: GPUDevice | null = null;
  private sdfGenPipeline: GPUComputePipeline | null = null;
  private aktifKapId: string | null = null;

  /**
   * Initialize the container manager with GPU device
   */
  basla(device: GPUDevice): void {
    this.device = device;
    console.log('[FikoSPHKapYonetici] Started with GPU device');
  }

  /**
   * Add a new container mesh and convert to SDF
   * @param id Unique identifier
   * @param ad Display name
   * @param vertices Float32Array of vertex positions (x,y,z,x,y,z,...)
   * @param indices Optional Uint32Array of triangle indices
   * @returns KapBilgi with SDF buffers
   */
  async kapEkle(id: string, ad: string, vertices: Float32Array, indices?: Uint32Array): Promise<KapBilgi> {
    if (!this.device) {
      throw new Error('[FikoSPHKapYonetici] Device not initialized. Call basla() first.');
    }

    console.log(`[FikoSPHKapYonetici] Adding container: ${ad} (${id})`);

    // Step 1: Compute AABB from vertices
    const aabb = this.hesaplaAABB(vertices);
    const boyut: Vec3 = {
      x: aabb.max.x - aabb.min.x,
      y: aabb.max.y - aabb.min.y,
      z: aabb.max.z - aabb.min.z
    };

    console.log(`[FikoSPHKapYonetici] AABB size: ${boyut.x.toFixed(3)}m x ${boyut.y.toFixed(3)}m x ${boyut.z.toFixed(3)}m`);

    // Step 2: Determine SDF resolution based on size
    const cozunurluk = this.hesaplaCozunurluk(boyut);
    console.log(`[FikoSPHKapYonetici] SDF resolution: ${cozunurluk[0]} x ${cozunurluk[1]} x ${cozunurluk[2]}`);

    // Step 3: Calculate cell size
    const cellSize = Math.max(
      boyut.x / cozunurluk[0],
      boyut.y / cozunurluk[1],
      boyut.z / cozunurluk[2]
    );

    // Step 4: Create SDF buffer
    const totalVoxels = cozunurluk[0] * cozunurluk[1] * cozunurluk[2];
    const sdfBuffer = this.device.createBuffer({
      label: `SDF Data: ${ad}`,
      size: totalVoxels * 4, // f32 per voxel
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC
    });

    // Step 5: Create SDF Info buffer
    const sdfInfo: SDFInfo = {
      originX: aabb.min.x,
      originY: aabb.min.y,
      originZ: aabb.min.z,
      cellSize: cellSize,
      dimX: cozunurluk[0],
      dimY: cozunurluk[1],
      dimZ: cozunurluk[2],
      totalCells: totalVoxels
    };

    const sdfInfoBuffer = this.device.createBuffer({
      label: `SDF Info: ${ad}`,
      size: 64, // Must match shader SDFInfo struct (4 * 16-byte alignment)
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    // Write SDF info to buffer (64 bytes, matching shader SDFInfo struct)
    const tempBuffer = new ArrayBuffer(64);
    const floatView = new Float32Array(tempBuffer);
    const uint32View = new Uint32Array(tempBuffer);
    // Row 0 (bytes 0-15): origin_xyz + cellSize
    floatView[0] = sdfInfo.originX;
    floatView[1] = sdfInfo.originY;
    floatView[2] = sdfInfo.originZ;
    floatView[3] = sdfInfo.cellSize;
    // Row 1 (bytes 16-31): dim_xyz + totalCells
    uint32View[4] = sdfInfo.dimX;
    uint32View[5] = sdfInfo.dimY;
    uint32View[6] = sdfInfo.dimZ;
    uint32View[7] = sdfInfo.totalCells;
    // Row 2-3 (bytes 32-63): padding (already zeroed)

    this.device.queue.writeBuffer(sdfInfoBuffer, 0, tempBuffer);

    // Step 6: Prepare triangle data for GPU
    const triangleData = this.hazirlaUcgenVerisi(vertices, indices);
    const triangleCount = triangleData.length / 12; // 3 vertices * 4 floats (vec4)

    console.log(`[FikoSPHKapYonetici] Triangle count: ${triangleCount}`);

    // Step 7: Create container info
    const kapBilgi: KapBilgi = {
      id,
      ad,
      sdfBuffer,
      sdfInfoBuffer,
      boyut,
      cozunurluk,
      hazir: false, // Will be set to true after SDF generation
      vertexCount: vertices.length / 3,
      triangleCount,
      aabbMin: aabb.min,
      aabbMax: aabb.max
    };

    this.kaplar.set(id, kapBilgi);

    // Step 8: Generate SDF (stub - actual GPU dispatch handled by Motor)
    // In production, this would:
    // 1. Create triangle vertex buffer
    // 2. Create SDF generation compute pipeline
    // 3. Dispatch compute shader (meshToSDF)
    // 4. Read back results and validate

    // For now, mark as ready (assuming SDF generation will be called externally)
    setTimeout(() => {
      kapBilgi.hazir = true;
      console.log(`[FikoSPHKapYonetici] Container ${ad} ready`);
    }, 100);

    // Set as active container if first one
    if (!this.aktifKapId) {
      this.aktifKapId = id;
      console.log(`[FikoSPHKapYonetici] Set ${ad} as active container`);
    }

    return kapBilgi;
  }

  /**
   * Remove a container and free GPU resources
   */
  kapSil(id: string): void {
    const kap = this.kaplar.get(id);
    if (!kap) {
      console.warn(`[FikoSPHKapYonetici] Container ${id} not found`);
      return;
    }

    console.log(`[FikoSPHKapYonetici] Removing container: ${kap.ad}`);

    // Destroy GPU buffers
    kap.sdfBuffer?.destroy();
    kap.sdfInfoBuffer?.destroy();

    this.kaplar.delete(id);

    // Clear active container if this was it
    if (this.aktifKapId === id) {
      this.aktifKapId = null;
    }
  }

  /**
   * Get the currently active container
   */
  getAktifKap(): KapBilgi | null {
    if (!this.aktifKapId) {
      return null;
    }
    return this.kaplar.get(this.aktifKapId) || null;
  }

  /**
   * Get SDF buffer for active container
   */
  getSdfBuffer(): GPUBuffer | null {
    const aktif = this.getAktifKap();
    return aktif?.sdfBuffer || null;
  }

  /**
   * Get SDF info buffer for active container
   */
  getSdfInfoBuffer(): GPUBuffer | null {
    const aktif = this.getAktifKap();
    return aktif?.sdfInfoBuffer || null;
  }

  /**
   * Set active container by ID
   */
  setAktifKap(id: string): boolean {
    if (!this.kaplar.has(id)) {
      console.warn(`[FikoSPHKapYonetici] Container ${id} not found`);
      return false;
    }

    this.aktifKapId = id;
    const kap = this.kaplar.get(id)!;
    console.log(`[FikoSPHKapYonetici] Active container: ${kap.ad}`);
    return true;
  }

  /**
   * Get all containers
   */
  getTumKaplar(): KapBilgi[] {
    return Array.from(this.kaplar.values());
  }

  /**
   * Calculate adaptive SDF resolution based on AABB size
   * Small containers (<10cm): 64³ voxels
   * Medium containers (10cm-50cm): 128³ voxels
   * Large containers (>50cm): 256³ voxels
   */
  hesaplaCozunurluk(aabbBoyut: Vec3): [number, number, number] {
    const maxDim = Math.max(aabbBoyut.x, aabbBoyut.y, aabbBoyut.z);

    let baseRes: number;
    if (maxDim < 0.1) {
      // Small: <10cm
      baseRes = 64;
    } else if (maxDim < 0.5) {
      // Medium: 10-50cm
      baseRes = 128;
    } else {
      // Large: >50cm
      baseRes = 256;
    }

    // Calculate aspect-ratio-aware resolution
    const aspectX = aabbBoyut.x / maxDim;
    const aspectY = aabbBoyut.y / maxDim;
    const aspectZ = aabbBoyut.z / maxDim;

    return [
      Math.max(16, Math.round(baseRes * aspectX)),
      Math.max(16, Math.round(baseRes * aspectY)),
      Math.max(16, Math.round(baseRes * aspectZ))
    ];
  }

  /**
   * Clean up all resources
   */
  temizle(): void {
    console.log('[FikoSPHKapYonetici] Cleaning up all containers');

    for (const [id, kap] of this.kaplar) {
      kap.sdfBuffer?.destroy();
      kap.sdfInfoBuffer?.destroy();
    }

    this.kaplar.clear();
    this.aktifKapId = null;
    this.sdfGenPipeline = null;
    this.device = null;
  }

  // ==================== Private Helper Methods ====================

  /**
   * Compute Axis-Aligned Bounding Box from vertices
   */
  private hesaplaAABB(vertices: Float32Array): { min: Vec3; max: Vec3 } {
    if (vertices.length < 3) {
      throw new Error('[FikoSPHKapYonetici] Not enough vertices for AABB calculation');
    }

    const min: Vec3 = {
      x: vertices[0],
      y: vertices[1],
      z: vertices[2]
    };

    const max: Vec3 = {
      x: vertices[0],
      y: vertices[1],
      z: vertices[2]
    };

    for (let i = 0; i < vertices.length; i += 3) {
      const x = vertices[i];
      const y = vertices[i + 1];
      const z = vertices[i + 2];

      min.x = Math.min(min.x, x);
      min.y = Math.min(min.y, y);
      min.z = Math.min(min.z, z);

      max.x = Math.max(max.x, x);
      max.y = Math.max(max.y, y);
      max.z = Math.max(max.z, z);
    }

    return { min, max };
  }

  /**
   * Prepare triangle data for GPU (convert to vec4 format)
   */
  private hazirlaUcgenVerisi(vertices: Float32Array, indices?: Uint32Array): Float32Array {
    let triangleData: Float32Array;

    if (indices) {
      // Indexed geometry
      const triangleCount = indices.length / 3;
      triangleData = new Float32Array(triangleCount * 3 * 4); // 3 vertices per tri, 4 floats per vertex (vec4)

      for (let i = 0; i < triangleCount; i++) {
        for (let j = 0; j < 3; j++) {
          const vertexIndex = indices[i * 3 + j];
          const outIndex = (i * 3 + j) * 4;

          triangleData[outIndex] = vertices[vertexIndex * 3];
          triangleData[outIndex + 1] = vertices[vertexIndex * 3 + 1];
          triangleData[outIndex + 2] = vertices[vertexIndex * 3 + 2];
          triangleData[outIndex + 3] = 0.0; // Padding
        }
      }
    } else {
      // Non-indexed geometry (assume vertices are already triangles)
      const vertexCount = vertices.length / 3;
      triangleData = new Float32Array(vertexCount * 4); // 4 floats per vertex (vec4)

      for (let i = 0; i < vertexCount; i++) {
        triangleData[i * 4] = vertices[i * 3];
        triangleData[i * 4 + 1] = vertices[i * 3 + 1];
        triangleData[i * 4 + 2] = vertices[i * 3 + 2];
        triangleData[i * 4 + 3] = 0.0; // Padding
      }
    }

    return triangleData;
  }

  /**
   * Create SDF generation compute pipeline (stub)
   * In production, this would create the actual compute pipeline using meshToSDF shader
   */
  private async olusturSDFPipeline(): Promise<void> {
    if (!this.device) {
      throw new Error('[FikoSPHKapYonetici] Device not initialized');
    }

    // Stub: Pipeline creation would happen here
    // Would use SDF_MESH_TO_SDF_PASS shader from sph-sdf-shaders.wgsl.ts

    console.log('[FikoSPHKapYonetici] SDF generation pipeline created (stub)');
  }

  /**
   * Dispatch SDF generation compute shader (stub)
   */
  private async uretSDF(kapBilgi: KapBilgi, triangleBuffer: GPUBuffer): Promise<void> {
    if (!this.device || !this.sdfGenPipeline) {
      console.warn('[FikoSPHKapYonetici] Cannot generate SDF - pipeline not ready');
      return;
    }

    // Stub: Actual GPU dispatch would happen here
    // 1. Create bind group with triangleBuffer, sdfBuffer, sdfInfoBuffer
    // 2. Create command encoder
    // 3. Dispatch compute pass with workgroups based on total voxels
    // 4. Submit commands

    console.log(`[FikoSPHKapYonetici] SDF generation dispatched for ${kapBilgi.ad} (stub)`);
  }
}

/**
 * Helper function to create a simple box container
 */
export function kutusuzKapOlustur(genislik: number, yukseklik: number, derinlik: number): Float32Array {
  // Create a simple box mesh (12 triangles, 36 vertices)
  const w = genislik / 2;
  const h = yukseklik / 2;
  const d = derinlik / 2;

  return new Float32Array([
    // Bottom face (y = -h)
    -w, -h, -d,  w, -h, -d,  w, -h, d,
    -w, -h, -d,  w, -h, d,  -w, -h, d,

    // Top face (y = h)
    -w, h, -d,  w, h, d,  w, h, -d,
    -w, h, -d,  -w, h, d,  w, h, d,

    // Front face (z = d)
    -w, -h, d,  w, -h, d,  w, h, d,
    -w, -h, d,  w, h, d,  -w, h, d,

    // Back face (z = -d)
    -w, -h, -d,  w, h, -d,  w, -h, -d,
    -w, -h, -d,  -w, h, -d,  w, h, -d,

    // Left face (x = -w)
    -w, -h, -d,  -w, -h, d,  -w, h, d,
    -w, -h, -d,  -w, h, d,  -w, h, -d,

    // Right face (x = w)
    w, -h, -d,  w, h, d,  w, -h, d,
    w, -h, -d,  w, h, -d,  w, h, d,
  ]);
}
